import { CountryCode, isValidPhoneNumber as isValidPhoneNumberLib } from 'libphonenumber-js';
import isString from 'lodash/isString';
import { UseControllerProps } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';
import * as z from 'zod';
import { IdentityType } from '~/types/company-identity';
import { CountryIsoCode } from '~/types/country';
import {
  requiredTaxNumber,
  requiredVatNumber,
  visibleDestinationCode,
  visibleTaxNumber,
  visibleVatNumber,
} from '~/utils/counterparts';
import { parseValue } from '~/utils/numberInputs';

export const requiredRule = (errorMessage: string) =>
  ({
    required: { value: true, message: errorMessage },
  }) as const satisfies UseControllerProps['rules'];

export const emailRule = (errorMessage: string) =>
  ({
    validate: (value: string) => {
      if (!value) {
        return undefined;
      }

      return isEmail(value) ? undefined : errorMessage;
    },
  }) as const satisfies UseControllerProps['rules'];

export const phoneRule = (errorMessage: string) =>
  ({
    validate: (value: { code: string; label: string; phone: string; countryCode: string }) => {
      return isValidPhoneNumber(value.countryCode, value.phone) ? undefined : errorMessage;
    },
  }) as const satisfies UseControllerProps['rules'];

export const isItalianTaxNumber = (value: string): boolean => {
  return new RegExp(/^[\da-zA-Z]{16}$/).test(value);
};

export const isItalianVatNumber = (value: string): boolean => {
  return new RegExp(/^\d{11}$/).test(value);
};

export const isValidPhoneNumber = (countryCode: string, phone: string, defaultCountry: CountryCode = 'IT'): boolean => {
  return isValidPhoneNumberLib(countryCode + phone, defaultCountry);
};

export function validateDestinationCodeLength(
  destinationCode: string,
  country: CountryIsoCode,
  identityType: IdentityType | null,
): z.IssueData | undefined {
  if (!visibleDestinationCode(country, identityType)) {
    return;
  }
  if (identityType === IdentityType.Pa) {
    if (destinationCode.length > 6) {
      return {
        code: z.ZodIssueCode.too_big,
        origin: 'string',
        input: destinationCode,
        maximum: 6,
        inclusive: true,
        exact: true,
      };
    }
    if (destinationCode.length < 6) {
      return {
        code: z.ZodIssueCode.too_small,
        origin: 'string',
        input: destinationCode,
        minimum: 6,
        inclusive: true,
        exact: true,
      };
    }
  }
  if (identityType !== IdentityType.Pa) {
    if (destinationCode.length > 7) {
      return {
        code: z.ZodIssueCode.too_big,
        origin: 'string',
        input: destinationCode,
        maximum: 7,
        inclusive: true,
        exact: true,
      };
    }
    if (destinationCode.length < 7) {
      return {
        code: z.ZodIssueCode.too_small,
        origin: 'string',
        input: destinationCode,
        minimum: 7,
        inclusive: true,
        exact: true,
      };
    }
  }
}

export const validateDDTPrices = (
  document: {
    includePrices: boolean | null;
    items: { netPrice: string | number; grossPrice: string | number }[];
  },
  ctx: z.core.$RefinementCtx,
) => {
  const { includePrices, items } = document;
  items.forEach((item, index) => {
    if (includePrices) {
      const netPrice = isString(item.netPrice) ? parseValue(item.netPrice) : item.netPrice;
      const grossPrice = isString(item.grossPrice) ? parseValue(item.grossPrice) : item.grossPrice;

      if (isNaN(netPrice)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { issue: 'invalid_amount' },
          path: ['items', index, 'netPrice'],
        });
      }

      if (isNaN(grossPrice)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { issue: 'invalid_amount' },
          path: ['items', index, 'grossPrice'],
        });
      }
    } else {
      item.netPrice = '0';
      item.grossPrice = '0';
    }
  });
};

export const isValidAmount = (amountString: string, ctx: z.core.$RefinementCtx<string>) => {
  const amount = parseValue(amountString);

  if (isNaN(amount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_amount' },
    });
    return z.NEVER;
  }
  return amount;
};

export const isValidPrice = (amountString: string, ctx: z.core.$RefinementCtx<string>) => {
  const amount = parseValue(amountString);

  if (isNaN(amount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_amount' },
    });
    return '';
  }
  return amountString;
};

export const isValidPositiveAmount = (amountString: string, ctx: z.core.$RefinementCtx<string>) => {
  const amount = parseValue(amountString);

  if (isNaN(amount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_amount' },
    });
    return z.NEVER;
  }

  if (amount < 0) {
    ctx.addIssue({ code: z.ZodIssueCode.too_small, minimum: 0, inclusive: true, origin: 'number' });
  }

  return amountString;
};

/**
 * Wraps a string schema so that an empty or whitespace-only input is treated as
 * `null` (i.e. absent) instead of being validated. Non-empty values are trimmed
 * and passed through to the wrapped schema. Useful for optional text/number
 * fields whose inputs emit `""` rather than `null` when cleared.
 */
export const emptyToNull = <Output>(schema: z.ZodType<Output, string>) =>
  z.preprocess((value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }, schema.nullable());

export const isValidManagementDataNumber = (amountString: string, ctx: z.core.$RefinementCtx<string>) => {
  const amount = parseValue(amountString);

  if (isNaN(amount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_amount' },
    });
    return '';
  }

  return amountString;
};

export const isValidQuantity = (quantityString: string, ctx: z.core.$RefinementCtx<string>) => {
  const quantity = parseValue(quantityString);

  if (isNaN(quantity)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_amount' },
    });
    return '';
  }

  if (quantity < 0) {
    ctx.addIssue({ code: z.ZodIssueCode.too_small, minimum: 0, inclusive: true, origin: 'number' });
  }

  return quantityString;
};

export const isValidDiscount =
  (isAmount: boolean) =>
  (discountString: string, ctx: z.core.$RefinementCtx): string => {
    const discount = parseValue(discountString);

    if (isNaN(discount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: { issue: 'invalid_amount' },
        path: ['value'],
      });
      return '';
    }

    if (discount < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 0,
        inclusive: true,
        origin: 'number',
        path: ['value'],
      });
      return '';
    }

    if (discount > 100 && !isAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 100,
        inclusive: true,
        origin: 'number',
        path: ['value'],
      });
      return '';
    }

    return discountString;
  };

export const isValidTaxableAmount = (discountString: string, ctx: z.core.$RefinementCtx<string>) => {
  const discount = parseValue(discountString);

  if (isNaN(discount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_amount' },
    });
    return '0';
  }

  if (discount < 0) {
    ctx.addIssue({ code: z.ZodIssueCode.too_small, minimum: 0, inclusive: true, origin: 'number' });
    return '0';
  }

  return discountString;
};

export const refineBasicLatin = (value: string, ctx: z.RefinementCtx) => {
  // eslint-disable-next-line no-control-regex
  const basicLatinRegex = /[^A-z\u0000-\u007F]/g;
  const characters = [...new Set(Array.from(value))];
  const invalidCharacters = characters.filter((c) => basicLatinRegex.test(c));

  if (invalidCharacters.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_characters', characters: invalidCharacters },
    });
  }
};

export const refineLatin1 = (value: string, ctx: z.RefinementCtx) => {
  // eslint-disable-next-line no-control-regex
  const latin1Regex = /[^\u0000-\u00ff]]/g;
  const characters = [...new Set(Array.from(value))];
  const invalidCharacters = characters.filter((c) => latin1Regex.test(c));

  if (invalidCharacters.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_characters', characters: invalidCharacters },
    });
  }
};

export const defaultZero = (amountString: string) => {
  if (amountString === '') {
    return '0';
  }
  return amountString;
};

export const refinePostalCode = (
  { postalCode, country }: { postalCode: string; country: CountryIsoCode },
  ctx: z.RefinementCtx,
): void => {
  if (country === 'IT' && postalCode.length !== 5) {
    if (postalCode.length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        origin: 'string',
        minimum: 5,
        inclusive: true,
        exact: true,
        path: ['postalCode'],
      });
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      origin: 'string',
      maximum: 5,
      inclusive: true,
      exact: true,
      path: ['postalCode'],
    });
  }
};

export const refineDestinationCodeCharset = (value: string, ctx: z.RefinementCtx): void => {
  const destinationCodeRegex = /[^A-z\d]/g;
  const characters = [...new Set(Array.from(value))];
  const invalidCharacters = characters.filter((c) => destinationCodeRegex.test(c));

  if (invalidCharacters.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'invalid_characters', characters: invalidCharacters },
    });
  }
};

export const refineTaxNumberFormat = (
  { taxNumber, country }: { taxNumber: string; country: CountryIsoCode },
  ctx: z.RefinementCtx,
): void => {
  if (
    taxNumber !== '' &&
    visibleTaxNumber(country) &&
    !isItalianTaxNumber(taxNumber) &&
    !isItalianVatNumber(taxNumber)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxNumber'],
      params: { issue: 'invalid_format' },
    });
  }
};

export const refineVatNumberFormat = (
  { vatNumber, country, identityType }: { vatNumber: string; country: CountryIsoCode; identityType: IdentityType },
  ctx: z.RefinementCtx,
): void => {
  if (
    vatNumber !== '' &&
    country === 'IT' &&
    visibleVatNumber(country, identityType) &&
    !isItalianVatNumber(vatNumber)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['vatNumber'],
      params: { issue: 'invalid_format' },
    });
  }
};

export const refineTaxOrVatRequired = (
  { taxNumber, vatNumber }: { taxNumber: string; vatNumber: string },
  ctx: z.RefinementCtx,
): void => {
  if (taxNumber.length === 0 && vatNumber.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxNumber'],
      params: { issue: 'tax_or_vat_required' },
    });
  }
};

export const refineTaxNumberRequired = (
  { taxNumber, country, identityType }: { taxNumber: string; country: CountryIsoCode; identityType: IdentityType },
  ctx: z.RefinementCtx,
): void => {
  if (requiredTaxNumber(country, identityType) && taxNumber.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxNumber'],
      params: { issue: 'required' },
    });
  }
};

export const refineVatNumberRequired = (
  { vatNumber, country, identityType }: { vatNumber: string; country: CountryIsoCode; identityType: IdentityType },
  ctx: z.RefinementCtx,
): void => {
  if (requiredVatNumber(country, identityType) && vatNumber.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['vatNumber'],
      params: { issue: 'required' },
    });
  }
};

export const refineRequired = <T>(value: T | null, ctx: z.RefinementCtx): T => {
  if (value === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { issue: 'required' },
    });
    return z.NEVER;
  }

  return value;
};
